/**
 * Dashboard específico para CLIENTES
 * - Solo ve sucursales de su franquicia
 * - Selector de sucursal
 * - Barberos de la sucursal seleccionada
 * - Servicios de su franquicia
 * - Modal de ofertas activas al entrar
 */
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useOffer } from '@/contexts/OfferContext';
import { branchService } from '@/services/branchService';
import { listBarbers } from '@/services/barberService';
import { useService } from '@/contexts/ServiceContext';
import type { Branch, Barber, Service, Offer } from '@/types';
import { MapPin, Users, Scissors, Calendar, Loader2, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { OfferModal, useOfferModal } from '@/components/offers/OfferModal';
import { GoogleReviewCard } from '@/components/dashboard/GoogleReviewCard';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export function ClientDashboard() {
  const { t } = useTranslation();
  const { user, customClaims } = useAuth();
  const { getActiveOffer } = useOffer();
  const { services: allServices, listServices } = useService();
  const { hasShown, markAsShown } = useOfferModal();

  const [branches, setBranches] = useState<Branch[]>([]);
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  // Offer modal state
  const [activeOffer, setActiveOffer] = useState<Offer | null>(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const offerModalTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const offerLoadedRef = useRef(false);

  const userFranchiseId = user?.franchiseId || customClaims?.franchiseId;

  useEffect(() => {
    loadData();
  }, [userFranchiseId]);

  // Load Google Place ID for review card
  useEffect(() => {
    if (!userFranchiseId) return;
    getDoc(doc(db, 'franchises', userFranchiseId)).then((snap) => {
      if (snap.exists()) {
        setGooglePlaceId(snap.data().googlePlaceId || null);
      }
    });
  }, [userFranchiseId]);

  // Load active offer and show modal
  useEffect(() => {
    // Prevent loading twice (StrictMode protection)
    if (offerLoadedRef.current) return;

    const loadActiveOffer = async () => {
      if (!userFranchiseId) return;

      try {
        const offer = await getActiveOffer(userFranchiseId);
        // Check hasShown again right before showing (avoids race conditions)
        if (offer && !hasShown(offer.offerId)) {
          offerLoadedRef.current = true;
          setActiveOffer(offer);
          // Small delay to let the dashboard render first
          offerModalTimeoutRef.current = setTimeout(() => {
            // Double check it hasn't been shown already
            if (!hasShown(offer.offerId)) {
              setShowOfferModal(true);
            }
          }, 500);
        }
      } catch (error) {
        console.error('Error loading active offer:', error);
      }
    };

    loadActiveOffer();

    // Cleanup timeout on unmount
    return () => {
      if (offerModalTimeoutRef.current) {
        clearTimeout(offerModalTimeoutRef.current);
      }
    };
  }, [userFranchiseId, getActiveOffer, hasShown]);

  useEffect(() => {
    if (selectedBranchId) {
      loadBranchBarbers(selectedBranchId);
    }
  }, [selectedBranchId]);

  const loadData = async () => {
    if (!userFranchiseId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Load branches of user's franchise
      const allBranches = await branchService.list();
      const userBranches = allBranches.filter(b => b.franchiseId === userFranchiseId);
      setBranches(userBranches);

      // Auto-select first branch
      if (userBranches.length > 0 && !selectedBranchId) {
        setSelectedBranchId(userBranches[0].branchId);
      }

      // Load services of user's franchise
      await listServices();
      const userServices = (allServices || []).filter((s: Service) => s.franchiseId === userFranchiseId);
      setServices(userServices);
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBranchBarbers = async (branchId: string) => {
    try {
      const allBarbers = await listBarbers(branchId);
      setBarbers(allBarbers);
    } catch (error) {
      console.error('Error loading barbers:', error);
    }
  };

  const handleCloseOfferModal = () => {
    if (activeOffer) {
      markAsShown(activeOffer.offerId);
    }
    setShowOfferModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-ink-subtle animate-spin mx-auto mb-4" aria-hidden="true" />
          <p className="text-ink-muted">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl sm:text-3xl text-ink mb-1">
          {t('dashboard.greeting', { name: user?.displayName || 'Usuario' })}
        </h1>
        <p className="text-sm text-ink-muted">
          Reserva tu turno y gestiona tus citas
        </p>
      </div>

      {/* Primary Action: Tomar Turno (giant, full width) — the single yellow CTA */}
      <Link
        to="/take-ticket"
        className="block w-full px-6 py-8 bg-accent text-on-accent rounded-xl hover:bg-accent-hover transition-colors focus:outline-none focus:ring-2 focus:ring-focus"
      >
        <div className="flex flex-col items-center text-center">
          <Calendar className="w-12 h-12 mb-3" aria-hidden="true" />
          <h3 className="font-sans text-2xl font-bold mb-1">Tomar Turno</h3>
          <p className="text-sm opacity-80">Reserva tu próximo corte</p>
        </div>
      </Link>

      {/* Secondary Quick Actions — neutral surface cards, not competing colors */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Link
          to="/client-queue"
          className="group p-6 bg-surface border border-hairline rounded-xl hover:border-hairline-strong transition-colors"
        >
          <Users className="w-7 h-7 mb-2 text-ink-subtle" aria-hidden="true" />
          <h3 className="font-sans text-base font-semibold text-ink mb-1 flex items-center gap-1">
            Mis Turnos <ChevronRight className="w-4 h-4 text-ink-subtle group-hover:text-ink transition-colors" aria-hidden="true" />
          </h3>
          <p className="text-sm text-ink-muted">Ver mis reservas activas</p>
        </Link>

        {/* Appointments - Only for VIP clients */}
        {(user?.isVIP || customClaims?.isVIP) && (
          <Link
            to="/appointments"
            className="group p-6 bg-surface border border-hairline rounded-xl hover:border-hairline-strong transition-colors"
          >
            <Calendar className="w-7 h-7 mb-2 text-ink-subtle" aria-hidden="true" />
            <h3 className="font-sans text-base font-semibold text-ink mb-1 flex items-center gap-1">
              Historial <ChevronRight className="w-4 h-4 text-ink-subtle group-hover:text-ink transition-colors" aria-hidden="true" />
            </h3>
            <p className="text-sm text-ink-muted">Ver mis citas anteriores</p>
          </Link>
        )}

        <Link
          to="/profile"
          className="group p-6 bg-surface border border-hairline rounded-xl hover:border-hairline-strong transition-colors"
        >
          <Users className="w-7 h-7 mb-2 text-ink-subtle" aria-hidden="true" />
          <h3 className="font-sans text-base font-semibold text-ink mb-1 flex items-center gap-1">
            Mi Perfil <ChevronRight className="w-4 h-4 text-ink-subtle group-hover:text-ink transition-colors" aria-hidden="true" />
          </h3>
          <p className="text-sm text-ink-muted">Configuración y puntos</p>
        </Link>
      </div>

      {/* Branch Selector */}
      {branches.length > 0 && (
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <h2 className="font-sans text-lg font-semibold text-ink mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
            Selecciona una Sucursal
          </h2>
          <select
            value={selectedBranchId || ''}
            onChange={(e) => setSelectedBranchId(e.target.value)}
            className="w-full px-4 py-3 bg-surface-3 border border-hairline rounded-lg text-ink focus:outline-none focus:ring-2 focus:ring-focus focus:border-transparent"
          >
            {branches.map((branch) => (
              <option key={branch.branchId} value={branch.branchId}>
                {branch.name} - {branch.city}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Barbers of Selected Branch */}
      {selectedBranchId && barbers.length > 0 && (
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <h2 className="font-sans text-lg font-semibold text-ink mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
            Barberos Disponibles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {barbers.map((barber) => (
              <button
                key={barber.barberId}
                type="button"
                aria-label={barber.displayName}
                className="p-4 bg-surface-2 rounded-lg border border-hairline hover:border-accent/60 transition-colors text-left w-full"
              >
                <div className="flex items-center gap-3">
                  {barber.photoURL ? (
                    <img
                      src={barber.photoURL}
                      alt={barber.displayName}
                      className="w-12 h-12 rounded-full object-cover ring-1 ring-hairline"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-surface-3 rounded-full flex items-center justify-center text-ink-muted font-semibold text-lg">
                      {barber.displayName.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="font-sans font-semibold text-ink">{barber.displayName}</h3>
                    {barber.specialties && barber.specialties.length > 0 && (
                      <p className="text-sm text-ink-muted">
                        {barber.specialties.slice(0, 2).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Google Review */}
      {googlePlaceId && (
        <GoogleReviewCard googlePlaceId={googlePlaceId} />
      )}

      {/* Services */}
      {services.length > 0 && (
        <div className="bg-surface border border-hairline rounded-xl p-6">
          <h2 className="font-sans text-lg font-semibold text-ink mb-4 flex items-center gap-2">
            <Scissors className="w-5 h-5 text-ink-subtle" aria-hidden="true" />
            Servicios Disponibles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.slice(0, 6).map((service) => (
              <div
                key={service.serviceId}
                className="p-4 bg-surface-2 rounded-lg border border-hairline"
              >
                <h3 className="font-sans font-semibold text-ink mb-1">{service.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink-muted">{service.duration} min</span>
                  <span className="font-semibold text-ink tabular-nums">
                    €{(service.price / 100).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Offer Modal */}
      {showOfferModal && activeOffer && (
        <OfferModal offer={activeOffer} onClose={handleCloseOfferModal} />
      )}
    </div>
  );
}
